import { useCallback, useEffect, useState } from 'react'
import { voiceApi, type VoicePresenceEntry } from '../services/api'

export type VoicePresenceMap = Map<string, VoicePresenceEntry>

/**
 * Polls `/api/voice/presence` (every 12 s, and on tab re-focus) and returns a
 * `userId -> presence` map so any surface — the public profile, the People
 * roster, the person drawer — can show "in <channel> · Join" (or "in voice ·
 * no access") for whoever's currently live in a voice channel. Only users
 * actually in a channel appear in the map.
 */
export function useVoicePresence(): { byUserId: VoicePresenceMap; refresh: () => void } {
  const [byUserId, setByUserId] = useState<VoicePresenceMap>(new Map())

  const fetchOnce = useCallback(async () => {
    try {
      const res = await voiceApi.presence()
      const next = new Map<string, VoicePresenceEntry>()
      for (const p of res.data.presence) next.set(p.userId, p)
      setByUserId(next)
    } catch {
      /* keep the last good map on a transient failure */
    }
  }, [])

  useEffect(() => {
    fetchOnce()
    const t = setInterval(fetchOnce, 12_000)
    const onVis = () => { if (!document.hidden) fetchOnce() }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      clearInterval(t)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [fetchOnce])

  return { byUserId, refresh: fetchOnce }
}
